
import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SignInForm from "./SignInForm";
import SignUpForm from "./SignUpForm";
import ConfirmationForm from "./ConfirmationForm";
import ResetPasswordForm from "./ResetPasswordForm";

type AuthMode = "signin" | "signup" | "confirmation" | "reset-password";

const AuthForm = () => {
  const [mode, setMode] = useState<AuthMode>("signin");
  const [email, setEmail] = useState("");

  const switchToMode = (newMode: string) => {
    setMode(newMode as AuthMode);
  };

  const toggleMode = () => {
    const newMode = mode === "signin" ? "signup" : "signin";
    setMode(newMode as AuthMode);
    // Reset form when switching modes
    setEmail("");
  };

  // Different card UI based on the current mode
  if (mode === "reset-password") {
    return (
      <Card className="w-full max-w-md mx-auto overflow-hidden animate-scale-in shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-3xl font-bold tracking-tight">
            Reset Password
          </CardTitle>
          <CardDescription>
            Enter your email to receive a password reset link
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ResetPasswordForm 
            switchToMode={switchToMode} 
            email={email} 
            setEmail={setEmail} 
          />
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button
            variant="link"
            className="text-clustr-text hover:text-clustr-blue transition-colors"
            onClick={() => setMode("signin")}
          >
            Back to Sign In
          </Button>
        </CardFooter>
      </Card>
    );
  }

  if (mode === "confirmation") {
    return (
      <Card className="w-full max-w-md mx-auto overflow-hidden animate-scale-in shadow-lg">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-3xl font-bold tracking-tight">
            Confirm Your Email
          </CardTitle>
          <CardDescription>
            We've sent a confirmation email to <span className="font-medium">{email}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <ConfirmationForm 
            switchToMode={switchToMode} 
            email={email}
          />
        </CardContent>
        <CardFooter className="flex justify-center">
          <Button
            variant="link"
            className="text-clustr-text hover:text-clustr-blue transition-colors"
            onClick={() => setMode("signin")}
          >
            Back to Sign In
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto overflow-hidden animate-scale-in shadow-lg">
      <CardHeader className="space-y-1 text-center">
        <CardTitle className="text-3xl font-bold tracking-tight">
          {mode === "signin" ? "Sign in to Clustr" : "Create your account"}
        </CardTitle>
        <CardDescription>
          {mode === "signin"
            ? "Enter your email and password to access your collections"
            : "Enter your details to create a new account"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {mode === "signin" ? (
          <SignInForm 
            switchToMode={switchToMode} 
            email={email} 
            setEmail={setEmail} 
          />
        ) : (
          <SignUpForm 
            switchToMode={switchToMode} 
            email={email} 
            setEmail={setEmail} 
          />
        )}
      </CardContent>
      <CardFooter className="flex justify-center">
        {mode === "signin" ? (
          <Button
            variant="outline"
            className="w-full h-12 border-clustr-blue text-clustr-blue hover:bg-clustr-blue/10 transition-colors"
            onClick={toggleMode}
          >
            <span className="flex items-center justify-center w-full">
              <span className="text-gray-600 mr-1">New to Clustr?</span>
              <span className="font-medium font-bold">Create Free Account!</span>
            </span>
          </Button>
        ) : (
          <Button
            variant="link"
            className="text-clustr-text hover:text-clustr-blue transition-colors"
            onClick={toggleMode}
          >
            Already have an account? Sign in
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default AuthForm;
